<script>
import { defineComponent, ref, computed, nextTick, watch } from 'vue'
import debounce from 'lodash/debounce'
import { useFetch } from '@/composable/useFetch'
import FormItem from '@/components/Input/FormItem.vue'
import FormInputGroup from '@/components/Input/FormInputGroup.vue'
import { getProductMasterList } from '@/api/product'
import { i18n } from '@/i18n'
import route from '@/router'

export default defineComponent({
  components: {
    FormInputGroup,
    FormItem,
  },
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    businessId: {
      type: String,
      default: '',
    },
    product: {
      // /** @type {import('vue').PropType<ProductFormModel} */
      type: Object,
      required: true,
      default: () => {},
    },
    business: {
      type: Object,
      required: true,
      default: () => {},
    },
    inventories: {
      type: Array,
      required: true,
      default: () => [],
    },
    errorBundling: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const removeFromBundle = (catalogId) => {
      const items = [...props.value].filter(bundle => {
        const unremoveItem = bundle.catalog_id !== catalogId
        if (!unremoveItem && bundle.message) emit('err', errorBundling.value - 1)
        return unremoveItem
      })
      emit('input', items)
    }

    const addNewItemModal = ref(false)
    const { execute: fetcher, isLoading, error, data } = useFetch()
    const errorMessage = computed(() => error.value?.response?.data?.message ?? error.value?.response?.statusText ?? error.value?.message)
    const errorBundling = computed(() => props.errorBundling)
    const optionHint = ref(null)
    const searchKeyword = ref('')
    const productSearchList = computed(() => {
      if(optionHint.value) {
        return []
      }

      const list = []
      const arr = data.value?.data ?? []

      arr.forEach((product, index) => {
        if(!product.is_bundling && product.bundling_type !== 'SOFT') {
          product.catalogs.forEach(catalog => {
            list.push({
              product_id: product.id,
              catalog_id: catalog.id,
              title:  catalog.title,
              sku: catalog.sku,
              warehouses: product?.warehouses || [],
            })
          })
        }
      })

      list.sort((a, b) => {
        const baseSKU = searchKeyword.value;
        const aMatches = a.sku.includes(baseSKU);
        const bMatches = b.sku.includes(baseSKU);
        if (aMatches && bMatches) {
            return a.sku.length - b.sku.length;
        }
        if (aMatches) return -1;
        if (bMatches) return 1;
        return 0;
      });

      return list
    })
    const onSearchProduct = debounce(async (keyword) => {
      const q = keyword.trim()
      searchKeyword.value = q
      if (!props.inventories.length) {
        optionHint.value = i18n.t('product.choose_warehouse_first')
        data.value = null
        return
      } else if (q.length < 2) {
        optionHint.value = i18n.t('product.search_min_2_chars')
        data.value = null
        return
      }

      optionHint.value = null
      const businessId = props.business.business_id
      fetcher(`/product/query`, {
        method: 'GET',
        headers: {
          'Business-Id': businessId,
        },
        params: {
          business_id: businessId,
          warehouse_id: props.inventories.map(inventory => inventory.id).join(),
          limit: 99,
          page: 1,
          q: keyword,
          is_bundling: false,
        },
      })
    }, 250)

    const onCheckWarehouses = (warehouses) => {
      if (warehouses.length >= props.inventories.length) {
        let countWarehouse = 0
        props.inventories.forEach(inv => {
          const warehouseIndex = warehouses.findIndex(whs => whs === inv.id)
          if (warehouseIndex > -1) countWarehouse++
        })
        if (countWarehouse >= props.inventories.length) return ''
      }
      return i18n.t('product.sku_not_in_warehouse')
    }

    const onSelectProductSearchItem = (catalogId) => {
      const items = [...props.value]
      const catalog = productSearchList.value.find((item) => item.catalog_id === catalogId)
      const existing = items.find((item) => item.catalog_id === catalogId)

      if (catalog) {
        if (existing) {
          existing.quantity += 1
          addNewItemModal.value = false
          nextTick(() => {
            const el = document.querySelector(`.bundle-item__${existing.catalog_id}`)
            if (el) {
              el.classList.add('flashing')

              setTimeout(() => el.classList.remove('flashing'), 1800)
            }
          })
        } else {
          const { catalog_id, product_id, sku, title, warehouses } = catalog
          const message = onCheckWarehouses(warehouses)
          if (message) emit('err', errorBundling.value + 1)

          items.push({
            quantity: 1,
            catalog_id,
            product_id,
            sku,
            title,
            warehouses,
            message,
          })

          addNewItemModal.value = false
        }

        emit('input', items)
      }
    }

    const onChangeQty = (item, value) => {
      const items = [...props.value].map(i => ({...i}))
      const existing = items.find((i) => i.catalog_id === item.catalog_id)

      if (existing) {
        existing.quantity = value
        emit('input', items)
      }
    }

    const baseUnit = computed(() => props.product.units ? props.product.units[0]?.unit : '-' )

    const onResetForm = () => {
      emit('input', [])
      emit('err', 0)
      onSetOptionHint()
    }

    const onSetOptionHint = () => {
      optionHint.value = props.inventories.length
        ? i18n.t('product.search_min_2_chars')
        : i18n.t('product.choose_warehouse_first')
      data.value = null
    }

    const onSetItems = debounce(async (inventories) => {
      await getProductMasterList({
        business_id: props.business.business_id,
        params: {
          business_id: props.business.business_id,
          warehouse_id: inventories.map(inventory => inventory.id).join(),
          product_ids: props.value.map(item => item.product_id).join(),
          page: 1,
          limit: 99,
          is_bundling: true,
        },
      })
      .then(({ data: { data: response } }) => {
        let errorCount = 0
        let filteredItems = []
        if (response?.length) {
          response.forEach(product => {
            product?.catalogs?.forEach(catalog => {
              const findItem = props.value.find(item => item.product_id === product.id && item.catalog_id === catalog.id)
              if (findItem?.catalog_id) {
                const message = onCheckWarehouses(findItem.warehouses)
                filteredItems.push({ ...findItem, message })
                if (message) errorCount++
              }
            })
          })
        }
        onSetOptionHint()
        emit('input', filteredItems)
        emit('err', errorCount)
      })
      .catch(() => onResetForm())
    })

    watch(() => props.inventories, (val) => {
      if (route.app._route.name === 'product.create') {
        if (val.length && props.value.length) {
          onSetItems(val)
        } else {
          onResetForm()
        }
      }
    })

    return {
      productSearchList,
      onSearchProduct,
      addNewItemModal,
      onSelectProductSearchItem,
      removeFromBundle,
      isLoading,
      errorMessage,
      baseUnit,
      onChangeQty,
      optionHint,
      onSetOptionHint,
    }
  },
})
</script>

<template>
  <div>
    <table class="w-100">
      <thead>
        <tr>
          <th>{{ $t('product.product_name') }}</th>
          <th>{{ $t('utils.quantity') }}</th>
          <th v-if="businessId" />
        </tr>
      </thead>
      <tbody>
        <tr v-if="!value.length">
          <td colspan="2" class="py-4">
            <a-empty>
              <template slot="description">
                <div class="">
                  {{ $t('product.bundle_list_empty') }}
                </div>
              </template>
            </a-empty>
          </td>
        </tr>
        <tr 
          v-for="item in value"
          :key="item.catalog_id"
          :class="{
            [`bundle-item__${item.catalog_id}`]: true,
          }"
        >
          <td>
            <div>{{ item.title }}</div>
            <div style="font-size: 90%; font-weight: 600">
              SKU: {{ item.sku }}
            </div>
            <div v-if="item.message" style="font-size: 85%; color: red">
              {{ item.message }}
            </div>
          </td>
          <td width="200">
            <FormItem tag="div" class="mb-0 form-item-no-margin">
              <FormInputGroup>
                <a-input-number
                  :value="item.quantity"
                  :min="1"
                  :step="1"
                  class="w-100"
                  @change="(value) => onChangeQty(item, value)"
                />

                <template #append>
                  <div class="ant-input-group-addon">
                    {{ item.uom || baseUnit }}
                  </div>
                </template>
              </FormInputGroup>
            </FormItem>
          </td>
          <td v-if="businessId">
            <a-popconfirm
              placement="left" 
              :title="$t('product.remove_bundle_item_confirm')"
              @confirm="() => removeFromBundle(item.catalog_id)"
            >
              <a-button class="btn-delete">
                <a-icon type="delete" />
              </a-button>
            </a-popconfirm>
          </td>
        </tr>
      </tbody>
    </table>

    <a-button v-if="businessId" type="primary" ghost @click.prevent="addNewItemModal = true">
      <a-icon type="plus" />
      {{ $t('product.add_product') }}
    </a-button>


    <a-modal v-model="addNewItemModal" :footer="false" @cancel="onSetOptionHint">
      <template #title>
        <h5 class="mb-0">
          {{ $t('product.add_bundle_product') }}
        </h5>
      </template>

      <div class="font-weight-medium mb-2">
        {{ $t('product.search_product') }}
      </div>
      <div class="product-search-field">
        <a-auto-complete
          :key="addNewItemModal"
          class="w-100 h-48px"
          auto-focus
          :placeholder="$t('product.enter_product_title_sku')"
          @search="onSearchProduct"
          @select="onSelectProductSearchItem"
        >
          <template #dataSource>
            <template v-if="!optionHint">
              <a-select-option
                v-for="(item) in productSearchList"
                :key="item.catalog_id"
                :value="item.catalog_id"
              >
                <div>{{ item.title }}</div>
                <div style="font-size: 90%; font-weight: 600">
                  SKU: {{ item.sku }}
                </div>
              </a-select-option>
            </template>
            <a-select-option v-else key="hint" disabled>
              {{ optionHint }}
            </a-select-option>
          </template>
        </a-auto-complete>
        <a-icon v-if="isLoading" type="loading" class="loader" />
      </div>

      <div v-if="errorMessage" class="ant-form-explain text-danger">
        {{ errorMessage }}
      </div>
    </a-modal>
  </div>
</template>

<style scoped lang="scss">
.product-search-field {
  position: relative;

  .loader {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--kit-color-primary)
  }
}

table {
  width: 100%;
  margin-bottom: 1.5rem;

  th:nth-child(1),
  th:nth-child(2) {
    background-color: #f8f8f8;
  }

  th:nth-child(3) {
    width: 30px;
  }

  td:nth-child(1),
  td:nth-child(2),
  th:nth-child(1),
  th:nth-child(2) {
    padding: .5rem .75rem;
    border: 1px solid #f1f1f1;
  }
  .ant-input-group-wrapper {
    border: 2px solid transparent;
  }
}

.btn-delete {
  border: 0;

  &:hover,
  &:active,
  &:focus {
    color: var(--danger);
  }
}

.flashing {
  padding: 0px;

  .ant-input-group-wrapper {
    transition: all .2s ease-in;
    border-radius: 6px;
    animation: blink .3s step-end infinite alternate;
  }
}

@keyframes blink {
  50% {
    border-color: var(--danger);
  }
}
</style>
